import type { NewsStory } from '@carouselgg/nexus/news-front'
import type { EmbedData } from '@carouselgg/nexus/article-embed'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

export interface PlatformArticle {
  id: string
  slug: string
  title: string
  excerpt: string
  body: string
  cover_image_url: string | null
  author_name: string
  published_at: string | null
  tags: string[]
  is_featured: boolean
  read_time_minutes: number
  /** Live cards for the body; only on a single-article read. */
  embeds?: EmbedData[]
}

export interface PlatformArticlesResponse {
  articles: PlatformArticle[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface TopicCount {
  tag: string
  count: number
}

/**
 * Published platform articles, featured first then newest. `tag` narrows to
 * one topic; `q` searches title, excerpt and body.
 */
export async function listPlatformArticles(limit = 4, offset = 0, tag?: string | null, q?: string | null): Promise<PlatformArticlesResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (tag) params.set('tag', tag)
  if (q) params.set('q', q)
  const res = await fetch(`${BASE_URL}/content/platform/articles?${params}`)
  if (!res.ok) throw new Error(`Couldn't load articles (${res.status})`)
  return res.json()
}

/** Platform news topics, most used first. */
export async function listPlatformArticleTopics(): Promise<TopicCount[]> {
  const res = await fetch(`${BASE_URL}/content/platform/article-tags`)
  if (!res.ok) throw new Error(`Couldn't load topics (${res.status})`)
  const data: { tags: TopicCount[] } = await res.json()
  return data.tags
}

export async function getPlatformArticleBySlug(slug: string): Promise<PlatformArticle> {
  const res = await fetch(`${BASE_URL}/content/platform/articles/${encodeURIComponent(slug)}`)
  if (!res.ok) throw new Error('Article not found')
  return res.json()
}

/** A platform article as the Nexus news components take it. */
export function toStory(a: PlatformArticle): NewsStory {
  return {
    id: a.id,
    to: `/news/${a.slug}`,
    title: a.title,
    excerpt: a.excerpt,
    coverUrl: a.cover_image_url,
    kicker: a.tags[0],
    featured: a.is_featured,
    author: a.author_name,
    date: a.published_at,
    readTimeMinutes: a.read_time_minutes,
  }
}

/** The most-read platform articles over the last `days`. */
export async function listMostReadPlatformArticles(days = 7, limit = 5): Promise<PlatformArticle[]> {
  const res = await fetch(`${BASE_URL}/content/platform/most-read?days=${days}&limit=${limit}`)
  if (!res.ok) throw new Error(`Couldn't load most read (${res.status})`)
  const data: { articles: PlatformArticle[] } = await res.json()
  return data.articles
}

/**
 * Counts a read, once per article per browser tab. Best effort: a failed count
 * never bothers the reader.
 */
export function recordArticleView(articleId: string): void {
  const key = `carousel_viewed_${articleId}`
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    // Storage blocked: count it anyway.
  }
  fetch(`${BASE_URL}/content/article-views/${articleId}`, { method: 'POST', keepalive: true }).catch(() => undefined)
}
